export const AUTH_FLOW_STEPS = Object.freeze({
  PASSWORD: "password",
  NEW_PASSWORD: "newPassword",
  TOTP_ENROLLMENT: "totpEnrollment",
  TOTP_CHALLENGE: "totpChallenge",
});

export const AUTH_FLOW_ACTIONS = Object.freeze({
  RESET: "reset",
  REQUIRE_NEW_PASSWORD: "requireNewPassword",
  REQUIRE_TOTP_ENROLLMENT: "requireTotpEnrollment",
  REQUIRE_TOTP_CHALLENGE: "requireTotpChallenge",
});

export const createInitialAuthFlowState = () => ({
  step: AUTH_FLOW_STEPS.PASSWORD,
});

export const authFlowReducer = (state, action) => {
  switch (action.type) {
    case AUTH_FLOW_ACTIONS.RESET:
      return createInitialAuthFlowState();
    case AUTH_FLOW_ACTIONS.REQUIRE_NEW_PASSWORD:
      return {
        step: AUTH_FLOW_STEPS.NEW_PASSWORD,
        username: action.username,
        sessionToken: action.sessionToken,
      };
    case AUTH_FLOW_ACTIONS.REQUIRE_TOTP_ENROLLMENT:
      return {
        step: AUTH_FLOW_STEPS.TOTP_ENROLLMENT,
        username: action.username,
        enrollmentTicket: action.enrollmentTicket,
        expiresIn: action.expiresIn,
        secret: action.secret,
        otpauthUri: action.otpauthUri,
      };
    case AUTH_FLOW_ACTIONS.REQUIRE_TOTP_CHALLENGE:
      return {
        step: AUTH_FLOW_STEPS.TOTP_CHALLENGE,
        username: action.username,
        sessionToken: action.sessionToken,
      };
    default:
      return state;
  }
};

export const AUTH_RESPONSE_TYPES = Object.freeze({
  FINAL_TOKENS: "finalTokens",
  NEW_PASSWORD_REQUIRED: "newPasswordRequired",
  TOTP_ENROLLMENT_REQUIRED: "totpEnrollmentRequired",
  TOTP_CHALLENGE_REQUIRED: "totpChallengeRequired",
  UNSUPPORTED_CHALLENGE: "unsupportedChallenge",
  MALFORMED: "malformed",
});

const FINAL_ACCESS_TOKEN_KEYS = ["AccessToken", "accessToken"];

const isFinalTokenBundle = (data) =>
  Boolean(
    data &&
      typeof data === "object" &&
      !Array.isArray(data) &&
      FINAL_ACCESS_TOKEN_KEYS.some(
        (key) => typeof data[key] === "string" && data[key].length > 0,
      ),
  );

export const classifyAuthResponse = (data) => {
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    return { type: AUTH_RESPONSE_TYPES.MALFORMED };
  }

  const challenge = data.challenge;
  const session = data.session;

  if (challenge === "NEW_PASSWORD_REQUIRED") {
    if (typeof session === "string" && session.length > 0) {
      return {
        type: AUTH_RESPONSE_TYPES.NEW_PASSWORD_REQUIRED,
        sessionToken: session,
      };
    }

    return { type: AUTH_RESPONSE_TYPES.MALFORMED };
  }

  if (challenge === "MFA_ENROLLMENT_REQUIRED") {
    const enrollmentTicket = data.enrollment_ticket;
    const expiresIn = data.expires_in;
    if (
      typeof enrollmentTicket === "string" &&
      enrollmentTicket.length > 0 &&
      typeof expiresIn === "number" &&
      Number.isFinite(expiresIn) &&
      expiresIn > 0
    ) {
      return {
        type: AUTH_RESPONSE_TYPES.TOTP_ENROLLMENT_REQUIRED,
        enrollmentTicket,
        expiresIn,
      };
    }

    return { type: AUTH_RESPONSE_TYPES.MALFORMED };
  }

  if (challenge === "SOFTWARE_TOKEN_MFA") {
    if (typeof session === "string" && session.length > 0) {
      return {
        type: AUTH_RESPONSE_TYPES.TOTP_CHALLENGE_REQUIRED,
        sessionToken: session,
      };
    }

    return { type: AUTH_RESPONSE_TYPES.MALFORMED };
  }

  if (typeof challenge === "string" && challenge.length > 0) {
    return {
      type: AUTH_RESPONSE_TYPES.UNSUPPORTED_CHALLENGE,
      challenge,
    };
  }

  if (
    challenge !== undefined ||
    session !== undefined ||
    data.enrollment_ticket !== undefined ||
    data.expires_in !== undefined
  ) {
    return { type: AUTH_RESPONSE_TYPES.MALFORMED };
  }

  if (isFinalTokenBundle(data)) {
    return {
      type: AUTH_RESPONSE_TYPES.FINAL_TOKENS,
      token: data,
    };
  }

  return { type: AUTH_RESPONSE_TYPES.MALFORMED };
};
